import { getSrcSetUrls, getUrl } from 'src/utils/image';
import { createSelector } from 'reselect';
import { selectIsMobile, selectViewportScreenSizeSelector } from '@aveneo/frontmoon-viewport-watcher/lib/modules/Viewport';
import { BREAKPOINTS } from '@aveneo/frontmoon-viewport-watcher/lib/components/constants';
import { prepareSchedule } from 'src/utils/schedule';
import { BILL_TILE_TYPE } from '../Order';
import { selectFingerprint } from '../User';

export const selectRestaurantInfoVisibility = (state) => state.restaurant.isInfoVisible;

export const selectIsInitalized = (state) => state.restaurant.isInitialized;

export const selectIsInfoRead = (state) => state.restaurant.isInfoRead;

export const selectRestaurant = (state) => state.restaurant.restaurant;

export const selectRestaurantSchedule = (state) => state.restaurant.restaurant?.schedule;

export const selectRestaurantTableName = (state) => state.restaurant.tableName;

export const selectRestaurantTableNumber = (state) => state.restaurant.tableNumber;

export const selectCallWaiterNotification = (state) => state.restaurant.showCallWaiterNotification;

export const selectWaiterCalledError = (state) => state.restaurant.waiterCallError;

export const selectRestaurantColors = (state) => state.restaurant.restaurant?.colors;

export const selectCode = (state) => state.restaurant.code;

export const selectLastBill = (state) => state.restaurant.lastBill;

export const selectLastOrders = (state) => state.restaurant.lastBill.orders;

export const selectRouteType = (state) => state.restaurant.routeType;

export const selectRestaurantTableSessionType = (state) => state.restaurant.tableSessionType;

export const selectIsNewMenuPublished = (state) => state.restaurant.isNewMenuPublished;

export const selectPublishNewMenuError = (state) => state.restaurant.publishNewMenuError;

export const selectRestaurantData = createSelector(
    selectRestaurant,
    (restaurant) => {
        if (!restaurant) {
            return restaurant;
        }

        const schedule = prepareSchedule(restaurant.schedule);

        return ({
            ...restaurant,
            schedule,
            isOpenTimeDefined: Boolean(schedule.days.length
                || schedule.fromTime
                || schedule.toTime
                || schedule.fromDate
                || schedule.toDate
                || schedule.everyYear)
        });
    }
);

export const selectIsRestaurantLoaded = createSelector(
    selectIsInitalized,
    selectRestaurant,
    (isInitialized, restaurant) => isInitialized ? Boolean(isInitialized && restaurant) : true
);

export const selectRestaurantSlug = createSelector(
    selectRestaurantData,
    (restaurant) => restaurant && restaurant.slug
);

export const selectRestaurantBackground = createSelector(
    selectRestaurant,
    selectViewportScreenSizeSelector,
    selectIsMobile,
    (restaurant, screenSize, isMobile) => {
        if (screenSize) {
            const resolution = BREAKPOINTS.DESKTOP_XL.name === screenSize
                ? { value: 1920 }
                : Object.values(BREAKPOINTS).find((breakpoint) => breakpoint.name === screenSize);

            const targetResolution = isMobile
                ? Math.round(resolution.value * 1.5)
                : resolution.value;

            if (restaurant?.backgroundId) {
                return getUrl(restaurant.id, restaurant.backgroundId, `rt:fill/size:${targetResolution}:${targetResolution}/g:ce/q:75`);
            }
        }

        return '/images/background.webp';
    }
);

export const selectRestaurantBackgroundSrcSet = createSelector(
    selectRestaurant,
    selectViewportScreenSizeSelector,
    selectIsMobile,
    (restaurant, screenSize, isMobile) => {
        if (screenSize) {
            const resolution = BREAKPOINTS.DESKTOP_XL.name === screenSize
                ? { value: 1920 }
                : Object.values(BREAKPOINTS).find((breakpoint) => breakpoint.name === screenSize);

            const targetResolution = isMobile
                ? Math.round(resolution.value * 1.5)
                : resolution.value;

            if (restaurant?.backgroundId) {
                return getSrcSetUrls(
                    restaurant.id, restaurant.backgroundId,
                    `rt:fill/size:${targetResolution}:${targetResolution}/g:ce/q:75`
                );
            }
        }

        return '';
    }
);

export const selectRestaurantBackgroundThumb = createSelector(
    selectRestaurant,
    (restaurant) => {
        if (restaurant?.backgroundId) {
            return getUrl(restaurant.id, restaurant.backgroundId, 'rt:fill/size:60:60/g:ce/q:85');
        }

        return '/images/background.webp';
    }
);

export const selectRestaurantBackgroundThumbSrcSet = createSelector(
    selectRestaurant,
    (restaurant) => {
        if (restaurant?.backgroundId) {
            return getSrcSetUrls(restaurant.id, restaurant.backgroundId, 'rt:fill/size:60:60/g:ce/q:85');
        }

        return '';
    }
);

export const selectRestaurantLogo = createSelector(
    selectRestaurant,
    (restaurant) => {
        if (restaurant?.logoId) {
            return getUrl(restaurant.id, restaurant.logoId, 'rt:fit/size:200:200/ex:1:ce/g:ce/q:85');
        }

        return '/images/apetitio-logo.png';
    }
);

export const selectRestaurantLogoSrcSet = createSelector(
    selectRestaurant,
    (restaurant) => {
        if (restaurant?.logoId) {
            return getSrcSetUrls(restaurant.id, restaurant.logoId, 'rt:fit/size:200:200/ex:1:ce/g:ce/q:85');
        }

        return '';
    }
);

export const selectRestaurantGallery = createSelector(
    selectRestaurantBackground,
    selectRestaurantBackgroundSrcSet,
    selectRestaurantBackgroundThumb,
    selectRestaurantBackgroundThumbSrcSet,
    (background, backgroundSrcSet, backgroundThumb, backgroundThumbSrcSet) => [{
        image: background,
        imageSrcSet: backgroundSrcSet,
        thumbnail: backgroundThumb,
        thumbnailSrcSet: backgroundThumbSrcSet
    }]
);

export const selectTableNameAndNumber = createSelector(
    selectRestaurantTableName,
    selectRestaurantTableNumber,
    (name, number) => ({
        name,
        number
    })
);

export const selectRestaurantName = createSelector(
    selectRestaurant,
    (restaurant) => restaurant?.name
);

export const selectLastBillSummary = createSelector(
    selectLastBill,
    (bill) => ({
        price: bill?.price || 0,
        positions: bill?.positionsNumber || 0
    })
);

export const selectLastBillOrders = createSelector(
    selectLastOrders,
    selectLastBill,
    selectLastBillSummary,
    selectFingerprint,
    (orders, bill, summary, fingerprint) => [
        {
            type: BILL_TILE_TYPE.SUMMARY,
            clients: bill.clients.sort((a, b) => {
                if ((a.fingerprint === fingerprint && b.fingerprint === fingerprint)
                    || (a.fingerprint !== fingerprint && b.fingerprint !== fingerprint)) {
                    return 0;
                }

                if (a.fingerprint === fingerprint && b.fingerprint !== fingerprint) {
                    return -1;
                }

                return 1;
            }),
            summary,
            id: 'summary',
            sumTranslationKey: 'App.orderSum'
        },
        ...orders]
);

export const selectRestaurantDescription = createSelector(
    selectRestaurantData,
    (restaurant) => restaurant?.description
);
